import React, { useState, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
    Form,
    Header,
    Segment,
    Button,
    Card,
    Modal,
    Grid,
    Divider,
    Input,
    Select,
    Checkbox,
} from 'semantic-ui-react'
import {
    FormTextFormField,
    FormDropdownField,
    AppPrimaryButton,
} from 'elements'
import {
    AppEditButton,
    AppRemoveButtonPopup,
    AppTable,
    FlexBox,
    FlexColumn,
    FlexRowBetween,
} from 'components'
import {
    GetCompanyTemplateList,
    GetCustomerList,
    useCustomerGroups,
    useNotification,
} from 'hooks'
import { AuthService } from 'utility'
import { useInvoiceSettings } from 'hooks/invoice-setting'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'

const RulesContainer = styled.div`
    padding: 1rem 0;
`

const GroupCardMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;

    .label {
        font-weight: 600;
        color: #334155;
        min-width: 110px;
        flex-shrink: 0;
    }

    .value {
        color: #0f172a;
        font-weight: 500;
    }
`

/* ---------------- DEFAULT VALUES ---------------- */

const defaultValues = {
    name: '',
    ruleStatus: true,
    triggerType: 'created',
    frequency: 'none',
    frequencyDayOfWeek: 'monday',
    frequencyDayOfMonth: 1,

    GroupId: '',
    customerId: '',
    templateId: '',
    locationId: '',
    groupingStrategy: 'none',

    billingModel: 'fixed_fee',
    fixedFeePerAudit: '',
    hourlyRate: '',
    flatMonthlyAmount: '',

    autoSendEmail: false,
}

const triggerTypeMap = {
    1: 'Created',
    2: 'Completed',
    3: 'Scheduled',
}

const RuleCard = ({ item, handleEdit, handleDelete }) => (
    <Card fluid className="mb-3">
        <Card.Content>
            <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                {item.RuleName}
            </Card.Header>
            <Card.Description className="mt-2">
                <GroupCardMetaRow>
                    <span className="label">Rule ID</span>
                    <span className="value">{item.InvoiceRuleId}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Status</span>
                    <span className="value">
                        {item.IsEnabled ? 'Active' : 'Inactive'}
                    </span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Trigger Type</span>
                    <span className="value">
                        {triggerTypeMap[item.TriggerType] || 'Scheduled'}
                    </span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Auto Send Email</span>
                    <span className="value">
                        {item.AutoSendEmail ? 'Yes' : 'No'}
                    </span>
                </GroupCardMetaRow>
                <FlexBox className="justify-content-end gap-2 mt-3">
                    <AppEditButton
                        onClick={() => handleEdit(item)}
                        type="button"
                    />
                    <AppRemoveButtonPopup
                        onConfirm={() => handleDelete(item)}
                    />
                </FlexBox>
            </Card.Description>
        </Card.Content>
    </Card>
)

const InvoiceRules = () => {
    const {
        rules,
        saveLoading,
        deleteLoading,
        saveInvoiceRule,
        deleteInvoiceRule,
        refetchRules,
        getInvoiceRule,
    } = useInvoiceSettings()

    const { successToast, errorToast } = useNotification()
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [selectedRule, setSelectedRule] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [loading] = useState(false)
    const [saveError] = useState(null)

    /* ---------------- DATA SOURCES ---------------- */

    const { options: templateOptions = [] } = GetCompanyTemplateList()
    const { options: customerList = [] } = GetCustomerList()

    const companyId = AuthService.getUser()?.Companies?.[0]?.CompanyId
    const customerGroups = useCustomerGroups(companyId)

    const groupOptions = useMemo(
        () =>
            (customerGroups || []).map(g => ({
                value: g.CustomerGroupId,
                text: g.CustomerGroupName,
            })),
        [customerGroups],
    )

    /* ---------------- FORM ---------------- */

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({ defaultValues })

    /* ---------------- OPTIONS ---------------- */

    const triggerTypeOptions = [
        { key: 'created', text: 'Created', value: 'created' },
        { key: 'completed', text: 'Completed', value: 'completed' },
        { key: 'scheduled', text: 'Scheduled', value: 'scheduled' },
    ]

    const frequencyOptions = [
        { key: 'none', text: 'None', value: 'none' },
        { key: 'weekly', text: 'Weekly', value: 'weekly' },
        { key: 'monthly', text: 'Monthly', value: 'monthly' },
    ]

    const dayOfWeekOptions = [
        { key: 'monday', text: 'Monday', value: 'monday' },
        { key: 'tuesday', text: 'Tuesday', value: 'tuesday' },
        { key: 'wednesday', text: 'Wednesday', value: 'wednesday' },
        { key: 'thursday', text: 'Thursday', value: 'thursday' },
        { key: 'friday', text: 'Friday', value: 'friday' },
        { key: 'saturday', text: 'Saturday', value: 'saturday' },
        { key: 'sunday', text: 'Sunday', value: 'sunday' },
    ]

    const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
        key: i + 1,
        text: `Day ${i + 1}`,
        value: i + 1,
    }))

    const billingModelOptions = [
        { key: 'fixed_fee', text: 'Fixed Fee', value: 'fixed_fee' },
        { key: 'hourly_rate', text: 'Hourly Rate', value: 'hourly_rate' },
        { key: 'flat_monthly', text: 'Flat Monthly', value: 'flat_monthly' },
    ]

    const groupingStrategyOptions = [
        { key: 'none', text: 'No Grouping', value: 'none' },
        { key: 'by_customer', text: 'Group By Customer', value: 'by_customer' },
        { key: 'by_location', text: 'Group By Location', value: 'by_location' },
    ]

    /* ---------------- SUBMIT ---------------- */

    const onSubmit = async data => {
        try {
            const payload = {
                InvoiceRuleId: isEditing ? selectedRule.InvoiceRuleId : 0,
                CompanyId: companyId,
                RuleName: data.name,
                IsEnabled: data.ruleStatus,
                TriggerType: (() => {
                    if (data.triggerType === 'created') return 1
                    if (data.triggerType === 'completed') return 2
                    return 3
                })(),
                FrequencyDayOfWeek:
                    data.frequency === 'weekly'
                        ? data.frequencyDayOfWeek
                        : null,
                FrequencyDayOfMonth:
                    data.frequency === 'monthly'
                        ? data.frequencyDayOfMonth
                        : null,
                GroupId: data.GroupId || null,
                CustomerId: data.customerId || null,
                TemplateId: data.templateId || null,
                LocationId: data.locationId || null,
                BillingModel: (() => {
                    if (data.billingModel === 'fixed_fee') return 1
                    if (data.billingModel === 'hourly_rate') return 2
                    return 3
                })(),
                FixedFeePerAudit: data.fixedFeePerAudit
                    ? parseFloat(data.fixedFeePerAudit)
                    : null,
                HourlyRate: data.hourlyRate
                    ? parseFloat(data.hourlyRate)
                    : null,
                FlatMonthlyAmount: data.flatMonthlyAmount
                    ? parseFloat(data.flatMonthlyAmount)
                    : null,
                GroupingStrategy: (() => {
                    if (data.groupingStrategy === 'none') return 1
                    if (data.groupingStrategy === 'by_customer') return 2
                    return 3
                })(),
                AutoSendEmail: data.autoSendEmail,
                LastRunOn: isEditing ? selectedRule.lastRunOn : null,
            }

            const result = await saveInvoiceRule(payload)

            if (result.success) {
                successToast(
                    `Rule ${isEditing ? 'updated' : 'created'} successfully`,
                )
                reset(defaultValues)
                setIsEditing(false)
                setSelectedRule(null)
            } else {
                errorToast(result.error || 'Failed to save rule')
            }
        } catch (error) {
            console.error('Error saving rule:', error)
            errorToast('An error occurred while saving the rule')
        }
    }

    /* ---------------- EDIT / DELETE ---------------- */

    const handleEdit = async rule => {
        if (!rule || !rule.InvoiceRuleId) {
            errorToast('Invalid rule data')
            return
        }

        try {
            const result = await getInvoiceRule(rule.InvoiceRuleId)

            if (result && result.success && result.data) {
                const ruleData = result.data
                setSelectedRule(ruleData)
                setIsEditing(true)

                const getBillingModel = () => {
                    if (ruleData.BillingModel === 1) return 'fixed_fee'
                    if (ruleData.BillingModel === 2) return 'hourly_rate'
                    return 'flat_monthly'
                }

                const getGroupingStrategy = () => {
                    if (ruleData.GroupingStrategy === 1) return 'none'
                    if (ruleData.GroupingStrategy === 2) return 'by_customer'
                    return 'by_location'
                }

                const getTriggerType = () => {
                    if (ruleData.TriggerType === 1) return 'created'
                    if (ruleData.TriggerType === 2) return 'completed'
                    return 'scheduled'
                }

                const getFrequency = () => {
                    if (ruleData.FrequencyDayOfWeek) return 'weekly'
                    if (ruleData.FrequencyDayOfMonth) return 'monthly'
                    return 'none'
                }

                reset({
                    ...ruleData,
                    name: ruleData.RuleName || '',
                    ruleStatus: ruleData.IsEnabled,
                    triggerType: getTriggerType(),
                    frequency: getFrequency(),
                    frequencyDayOfWeek: ruleData.FrequencyDayOfWeek || 'monday',
                    frequencyDayOfMonth: ruleData.FrequencyDayOfMonth || 1,
                    GroupId: ruleData.GroupId || '',
                    customerId: ruleData.CustomerId || '',
                    templateId: ruleData.TemplateId || '',
                    locationId: ruleData.LocationId || '',
                    billingModel: getBillingModel(),
                    fixedFeePerAudit: ruleData.FixedFeePerAudit || '',
                    hourlyRate: ruleData.HourlyRate || '',
                    flatMonthlyAmount: ruleData.FlatMonthlyAmount || '',
                    autoSendEmail: ruleData.AutoSendEmail || false,
                    groupingStrategy: getGroupingStrategy(),
                })
            } else {
                errorToast('Failed to load rule data')
            }
        } catch (error) {
            console.error('Error loading rule:', error)
            errorToast('An error occurred while loading the rule')
        }
    }

    const handleDelete = async rule => {
        try {
            const result = await deleteInvoiceRule(rule.InvoiceRuleId || rule.id)
            if (result.success) {
                successToast('Rule deleted successfully')
                setDeleteModalOpen(false)
                refetchRules()
            } else {
                errorToast(result.error || 'Failed to delete rule')
            }
        } catch (error) {
            console.error('Error deleting rule:', error)
            errorToast('An error occurred while deleting the rule')
        }
    }

    const columns = useMemo(
        () => [
            {
                title: 'Rule ID',
                key: 'InvoiceRuleId',
                textAlign: 'center',
            },
            {
                title: 'Rule Name',
                key: 'RuleName',
                textAlign: 'center',
            },
            {
                title: 'Status',
                textAlign: 'center',
                render: rule => (rule.IsEnabled ? 'Active' : 'Inactive'),
            },
            {
                title: 'Trigger Type',
                textAlign: 'center',
                render: rule => triggerTypeMap[rule.TriggerType] || 'Scheduled',
            },
            {
                title: 'Auto Send Email',
                textAlign: 'center',
                render: rule => (rule.AutoSendEmail ? 'Yes' : 'No'),
            },
            {
                title: 'Actions',
                textAlign: 'center',
                render: rule => (
                    <FlexBox className="justify-content-center">
                        <AppEditButton
                            onClick={() => handleEdit(rule)}
                            type="button"
                        />
                        <AppRemoveButtonPopup
                            onConfirm={() => handleDelete(rule)}
                        />
                    </FlexBox>
                ),
            },
        ],
        [handleEdit, handleDelete],
    )

    return (
        <RulesContainer>
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <Header as="h2" className="m-0">
                        Invoice Rules
                        <Header.Subheader>
                            Automation rules for invoices
                        </Header.Subheader>
                    </Header>
                </FlexRowBetween>
            </DesktopMode>
            <MobileMode>
                <FlexColumn className="mb-3">
                    <Header as="h2" className="m-0">
                        Invoice Rules
                        <Header.Subheader>
                            Automation rules for invoices
                        </Header.Subheader>
                    </Header>
                </FlexColumn>
            </MobileMode>

            <Segment raised>
                <Form
                    onSubmit={handleSubmit(onSubmit)}
                    loading={loading}
                    error={!!saveError}
                >
                    {/* BASIC INFO */}
                    <Header as="h4">Basic Information</Header>
                    <Divider />

                    <Grid stackable>
                        <Grid.Column width={16}>
                            <FormTextFormField
                                name="name"
                                control={control}
                                label="Rule Name"
                                rules={{ required: 'Rule name is required' }}
                                error={errors.name}
                            />
                        </Grid.Column>
                    </Grid>

                    <Controller
                        name="ruleStatus"
                        control={control}
                        render={({ field }) => (
                            <Form.Field className="mt-2">
                                <Checkbox
                                    label="Rule is Active"
                                    checked={field.value}
                                    onChange={(_, d) =>
                                        field.onChange(d.checked)
                                    }
                                />
                            </Form.Field>
                        )}
                    />

                    {/* TRIGGER */}
                    <Header as="h4" className="mt-4">
                        Trigger & Frequency
                    </Header>
                    <Divider />
                    <Form.Group widths="equal">
                        <Controller
                            name="triggerType"
                            control={control}
                            render={({ field }) => (
                                <Form.Field>
                                    <label>Trigger Type</label>
                                    <Select
                                        value={field.value}
                                        options={triggerTypeOptions}
                                        onChange={(_, d) =>
                                            field.onChange(d.value)
                                        }
                                    />
                                </Form.Field>
                            )}
                        />

                        <Controller
                            name="frequency"
                            control={control}
                            render={({ field }) => (
                                <Form.Field>
                                    <label>Frequency</label>
                                    <Select
                                        value={field.value}
                                        options={frequencyOptions}
                                        onChange={(_, d) =>
                                            field.onChange(d.value)
                                        }
                                    />
                                </Form.Field>
                            )}
                        />

                        {watch('frequency') === 'weekly' && (
                            <Controller
                                name="frequencyDayOfWeek"
                                control={control}
                                render={({ field }) => (
                                    <Form.Field>
                                        <label>Day of Week</label>
                                        <Select
                                            value={field.value}
                                            options={dayOfWeekOptions}
                                            onChange={(_, d) =>
                                                field.onChange(d.value)
                                            }
                                        />
                                    </Form.Field>
                                )}
                            />
                        )}

                        {watch('frequency') === 'monthly' && (
                            <Controller
                                name="frequencyDayOfMonth"
                                control={control}
                                render={({ field }) => (
                                    <Form.Field>
                                        <label>Day of Month</label>
                                        <Select
                                            value={field.value}
                                            options={dayOfMonthOptions}
                                            onChange={(_, d) =>
                                                field.onChange(d.value)
                                            }
                                        />
                                    </Form.Field>
                                )}
                            />
                        )}
                    </Form.Group>
                    {/* SCOPE */}
                    <Header as="h4" className="mt-4">
                        Scope Filters
                    </Header>
                    <Divider />
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            name="customerId"
                            label="Customers"
                            options={customerList}
                            errors={errors.customerId}
                        />

                        <FormDropdownField
                            control={control}
                            name="GroupId"
                            label="Group"
                            options={groupOptions}
                            errors={errors.GroupId}
                        />

                        <FormDropdownField
                            control={control}
                            name="templateId"
                            label="Template"
                            options={templateOptions}
                            errors={errors.templateId}
                        />
                    </Form.Group>

                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            name="groupingStrategy"
                            label="Grouping Strategy"
                            options={groupingStrategyOptions}
                            errors={errors.groupingStrategy}
                        />
                    </Form.Group>
                    {/* BILLING */}
                    <Header as="h4" className="mt-4">
                        Billing Model
                    </Header>
                    <Divider />
                    <Form.Group widths="equal">
                        <Controller
                            name="billingModel"
                            control={control}
                            render={({ field }) => (
                                <Form.Field>
                                    <label>Billing Type</label>
                                    <Select
                                        value={field.value}
                                        options={billingModelOptions}
                                        onChange={(_, d) =>
                                            field.onChange(d.value)
                                        }
                                    />
                                </Form.Field>
                            )}
                        />

                        {watch('billingModel') === 'fixed_fee' && (
                            <Controller
                                name="fixedFeePerAudit"
                                control={control}
                                render={({ field }) => (
                                    <Form.Field>
                                        <label>Fixed Fee Per Audit</label>
                                        <Input type="number" {...field} />
                                    </Form.Field>
                                )}
                            />
                        )}

                        {watch('billingModel') === 'hourly_rate' && (
                            <Controller
                                name="hourlyRate"
                                control={control}
                                render={({ field }) => (
                                    <Form.Field>
                                        <label>Hourly Rate</label>
                                        <Input type="number" {...field} />
                                    </Form.Field>
                                )}
                            />
                        )}

                        {watch('billingModel') === 'flat_monthly' && (
                            <Controller
                                name="flatMonthlyAmount"
                                control={control}
                                render={({ field }) => (
                                    <Form.Field>
                                        <label>Flat Monthly Amount</label>
                                        <Input type="number" {...field} />
                                    </Form.Field>
                                )}
                            />
                        )}
                    </Form.Group>

                    {/* EXTRA */}
                    <Divider />
                    <Controller
                        name="autoSendEmail"
                        control={control}
                        render={({ field }) => (
                            <Form.Field>
                                <Checkbox
                                    label="Auto Send Email"
                                    checked={field.value}
                                    onChange={(_, d) =>
                                        field.onChange(d.checked)
                                    }
                                />
                            </Form.Field>
                        )}
                    />

                    <Divider />
                    <FlexBox className="mt-3 justify-content-end">
                        <AppPrimaryButton
                            type="submit"
                            primary
                            loading={loading || saveLoading}
                            disabled={loading || saveLoading}
                        >
                            {isEditing ? 'Update Rule' : 'Create Rule'}
                        </AppPrimaryButton>
                    </FlexBox>

                    {saveError && (
                        <div style={{ color: 'red', marginTop: '10px' }}>
                            {saveError}
                        </div>
                    )}
                </Form>
            </Segment>

            {/* LIST */}
            <Segment raised>
                <AppTable
                    columns={columns}
                    data={rules || []}
                    emptyMessage="No Invoice Rules Found"
                    hidePagination
                    mobileCard={
                        <RuleCard
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}
                        />
                    }
                />
            </Segment>

            {/* DELETE MODAL */}
            <Modal open={deleteModalOpen} size="tiny">
                <Header icon="trash" content="Delete Rule" />
                <Modal.Content>
                    Are you sure you want to delete this rule?
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={() => setDeleteModalOpen(false)}
                        disabled={deleteLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        onClick={handleDelete}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                    >
                        Delete
                    </Button>
                </Modal.Actions>
            </Modal>
        </RulesContainer>
    )
}

export default InvoiceRules
